import _Add from "@mui/icons-material/Add";
import _Bookmark from "@mui/icons-material/Bookmark";
import _Hotel from "@mui/icons-material/Hotel";
import _CircularProgress from "@mui/material/CircularProgress";
import _Box from "@mui/material/Box";
import _Phone from "@mui/icons-material/Phone";
import _Email from "@mui/icons-material/Email";
import _Public from "@mui/icons-material/Public";
import _Typography from "@mui/material/Typography";
import _Tooltip from "@mui/material/Tooltip";
import _IconButton from "@mui/material/IconButton";
import _Close from "@mui/icons-material/Close";
import { ThemeProvider } from '@mui/material/styles';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import bicycleHomeLogo from '../../assets/images/poi/accueil-velo.png';
import { useClipboard } from '../../hooks';
import { Button } from '../button';
function PoiPopup({
  theme,
  categories: _categories,
  search,
  poiAnnotationsMap,
  poi,
  selectedTripStep: _selectedTripStep,
  widgetConfig,
  onSearchUpdated,
  onAddAsRideStep,
  onRemoveFromRideSteps,
  onPoiAddedToTripStep,
  onPoiRemovedFromTripStep,
  onTripStepAccomodationSelected,
  onTripStepAccomodationUnselected,
  onClose,
  enqueueSnackbar
}, ref) {
  const [poiAnnotation, setPoiAnnotation] = useState();
  const [selectedTripStep, selectTripStep] = useState(_selectedTripStep);
  const [updatingTripStep, updateTripStep] = useState(false);
  const {
    t
  } = useTranslation();
  const {
    copy: copyToClipboard
  } = useClipboard(() => enqueueSnackbar?.(t('commons.phone_copied_to_clipboard')), () => enqueueSnackbar?.(t('commons.failed_to_copy_phone_to_clipboard')));
  useEffect(() => {
    getPoiAnnotation();
  }, [selectedTripStep, poiAnnotationsMap]);
  function getPoiAnnotation() {
    if (!poiAnnotationsMap || !selectedTripStep || !selectedTripStep.poiAnnotationIds) return;
    const poiAnnotationId = selectedTripStep.poiAnnotationIds.find(id => {
      const poiAnnotation = poiAnnotationsMap[id];
      if (!poiAnnotation) return false;
      const {
        source,
        poi: {
          id: poiId,
          externalId
        }
      } = poiAnnotation;
      return source === 'geovelo' ? poiId === poi.id : externalId === poi.externalId;
    });
    setPoiAnnotation(poiAnnotationId && poiAnnotationsMap[poiAnnotationId] || null);
  }
  useImperativeHandle(ref, () => ({
    update: (_poiAnnotationsMap, _selectedTripStep) => {
      if (selectedTripStep && selectedTripStep.id === _selectedTripStep.id) {
        selectTripStep(_selectedTripStep);
        updateTripStep(false);
      }
    }
  }));
  const {
    title: _title,
    location,
    description,
    photos,
    categoryCodes,
    detailsUrl,
    website,
    phone,
    email,
    bicycleHome,
    isAccomodation
  } = poi;
  const photo = photos[0];
  const hasCategory = {};
  let categories = _categories.reduce((res, category) => {
    const {
      code,
      labelKey,
      children
    } = category;
    const label = code !== 'bicycle' && code !== 'tourism' && t(labelKey || '', {
      context: 'one'
    });
    for (const child of children) {
      const {
        code: childCode,
        labelKey: childLabelKey
      } = child;
      const childLabel = t(childLabelKey || '', {
        context: 'one'
      });
      if (childLabel && categoryCodes.indexOf(childCode) > -1 && !hasCategory[childLabel]) {
        res.push(child);
        hasCategory[childLabel] = true;
      }
    }
    if (label && categoryCodes.indexOf(code) > -1 && !hasCategory[label]) {
      res.push(category);
      hasCategory[label] = true;
    }
    return res;
  }, []);
  let title = _title;
  if (!_title) {
    const category = categories[0];
    if (category && category.labelKey) {
      title = t(category.labelKey, {
        context: 'one'
      });
      categories = categories.slice(1);
    }
  }
  const inTripStep = selectedTripStep && poiAnnotation && selectedTripStep.poiAnnotationIds ? selectedTripStep.poiAnnotationIds.indexOf(poiAnnotation.id) > -1 || selectedTripStep.departureAccomodationAnnotationId === poiAnnotation.id || selectedTripStep.arrivalAccomodationAnnotationId === poiAnnotation.id : false;
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(CloseButtonWrapper, null, /*#__PURE__*/React.createElement(_Tooltip, {
    placement: "left",
    title: /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.actions.close"
    })
  }, /*#__PURE__*/React.createElement(_IconButton, {
    onClick: () => onClose(),
    size: "small"
  }, /*#__PURE__*/React.createElement(_Close, null)))), photo && /*#__PURE__*/React.createElement(_Tooltip, {
    placement: "top",
    title: /*#__PURE__*/React.createElement(React.Fragment, null, photo.title, " ", photo.copyright && /*#__PURE__*/React.createElement(React.Fragment, null, "\xA9 ", photo.copyright))
  }, /*#__PURE__*/React.createElement(StyledPhotoWrapper, {
    url: photo.thumbnailUrl || ''
  })), /*#__PURE__*/React.createElement(StyledHeader, {
    className: photo ? '' : 'shifted'
  }, bicycleHome && /*#__PURE__*/React.createElement(StyledHeaderLogo, {
    title: "Accueil v\xE9lo",
    url: bicycleHomeLogo
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      overflowX: 'hidden'
    }
  }, detailsUrl ? /*#__PURE__*/React.createElement(StyledTitle, {
    color: "secondary",
    component: "a",
    href: detailsUrl,
    target: "_blank",
    title: title || '',
    variant: "h6"
  }, title) : /*#__PURE__*/React.createElement(StyledTitle, {
    title: title || '',
    variant: "h6"
  }, title), categories.length > 0 && /*#__PURE__*/React.createElement(_Typography, {
    color: "textSecondary",
    component: "p",
    variant: "caption"
  }, categories.map(({
    id,
    labelKey
  }, index) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: id
  }, index > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0\u2022\xA0"), /*#__PURE__*/React.createElement(Trans, {
    i18nKey: labelKey,
    values: {
      context: 'one'
    }
  })))))), (website || email || phone) && /*#__PURE__*/React.createElement(StyledActions, null, website && /*#__PURE__*/React.createElement(_Tooltip, {
    placement: "right",
    title: /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.website"
    })
  }, /*#__PURE__*/React.createElement(_IconButton, {
    color: "primary",
    component: "a",
    href: website,
    rel: "noreferrer",
    size: "small",
    target: "_blank"
  }, /*#__PURE__*/React.createElement(_Public, null))), email && /*#__PURE__*/React.createElement(_Tooltip, {
    placement: "right",
    title: /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.email"
    })
  }, /*#__PURE__*/React.createElement(_IconButton, {
    color: "primary",
    component: "a",
    href: `mailto:${email}`,
    size: "small"
  }, /*#__PURE__*/React.createElement(_Email, null))), phone && /*#__PURE__*/React.createElement(_Tooltip, {
    placement: "right",
    title: phone
  }, /*#__PURE__*/React.createElement(_IconButton, {
    color: "primary",
    onClick: () => copyToClipboard(phone),
    size: "small"
  }, /*#__PURE__*/React.createElement(_Phone, null)))), description && /*#__PURE__*/React.createElement(StyledContent, null, /*#__PURE__*/React.createElement(_Typography, {
    component: "p",
    variant: "caption"
  }, widgetConfig?.code === 'lav' && detailsUrl && description.length > 150 ? /*#__PURE__*/React.createElement(React.Fragment, null, description.slice(0, 150).trim(), "...", ' ', /*#__PURE__*/React.createElement(_Typography, {
    color: "secondary",
    component: "a",
    href: detailsUrl,
    target: "_blank",
    variant: "caption"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.actions.see_details"
  }))) : description)), search && onSearchUpdated && /*#__PURE__*/React.createElement(StyledActions, null, /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    onClick: () => {
      if (!search.wayPoints[0]) search.wayPoints.splice(0, 1, location.clone());else search.wayPoints.splice(0, 0, location.clone());
      onSearchUpdated();
      onClose();
    },
    variant: "contained"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.actions.leave_from_here"
  })), /*#__PURE__*/React.createElement(_Box, {
    flexGrow: 1
  }), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    onClick: () => {
      if (!search.wayPoints[1]) search.wayPoints.splice(1, 1, location.clone());else search.wayPoints.push(location.clone());
      onSearchUpdated();
      onClose();
    },
    variant: "contained"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.actions.go_there"
  }))), onAddAsRideStep && !search?.wayPoints.find(wayPoint => wayPoint && wayPoint.id === `poi-${poi.id}`) && /*#__PURE__*/React.createElement(StyledActions, null, /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    onClick: () => {
      onAddAsRideStep(poi);
      onClose();
    },
    variant: "contained"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.actions.add_as_step"
  }))), onRemoveFromRideSteps && search?.wayPoints.find(wayPoint => wayPoint && wayPoint.id === `poi-${poi.id}`) && /*#__PURE__*/React.createElement(StyledActions, null, /*#__PURE__*/React.createElement(Button, {
    className: "dangerous",
    color: "error",
    onClick: () => {
      onRemoveFromRideSteps(poi);
      onClose();
    },
    variant: "contained"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.actions.remove_from_steps"
  }))), selectedTripStep && /*#__PURE__*/React.createElement(StyledActions, null, isAccomodation ? inTripStep ? /*#__PURE__*/React.createElement(Button, {
    disabled: updatingTripStep,
    onClick: () => {
      if (poiAnnotation) onTripStepAccomodationUnselected?.(poiAnnotation);
      updateTripStep(true);
      onClose();
    },
    startIcon: updatingTripStep ? /*#__PURE__*/React.createElement(_CircularProgress, {
      color: "inherit",
      size: 16,
      thickness: 4
    }) : /*#__PURE__*/React.createElement(_Hotel, {
      color: "primary"
    }),
    variant: "outlined"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.trip.actions.remove_accomodation"
  })) : /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    disabled: updatingTripStep,
    onClick: () => {
      onTripStepAccomodationSelected?.(poi);
      updateTripStep(true);
      onClose();
    },
    startIcon: updatingTripStep ? /*#__PURE__*/React.createElement(_CircularProgress, {
      color: "inherit",
      size: 16,
      thickness: 4
    }) : /*#__PURE__*/React.createElement(_Hotel, null),
    variant: "contained"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.trip.actions.add_accomodation"
  })) : inTripStep ? /*#__PURE__*/React.createElement(Button, {
    disabled: updatingTripStep,
    onClick: () => {
      if (poiAnnotation) onPoiRemovedFromTripStep?.(poiAnnotation);
      updateTripStep(true);
    },
    startIcon: updatingTripStep ? /*#__PURE__*/React.createElement(_CircularProgress, {
      color: "inherit",
      size: 16,
      thickness: 4
    }) : /*#__PURE__*/React.createElement(_Bookmark, {
      color: "primary"
    }),
    variant: "outlined"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.trip.actions.to_visit"
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    disabled: updatingTripStep,
    onClick: () => {
      onPoiAddedToTripStep?.(poi);
      updateTripStep(true);
    },
    startIcon: updatingTripStep ? /*#__PURE__*/React.createElement(_CircularProgress, {
      color: "inherit",
      size: 16,
      thickness: 4
    }) : /*#__PURE__*/React.createElement(_Bookmark, null),
    variant: "outlined"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.trip.actions.to_visit"
  })), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    disabled: updatingTripStep,
    onClick: () => {
      if (onPoiAddedToTripStep) onPoiAddedToTripStep(poi, true);
      onClose();
    },
    startIcon: /*#__PURE__*/React.createElement(_Add, null),
    variant: "contained"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.trip.actions.add_step"
  }))))));
}
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: max(calc(50vh - 24px), 400px);
  overflow-y: hidden;
  position: relative;
  width: min(100vw - 120px, 350px);
`;
const CloseButtonWrapper = styled.div`
  padding: 8px;
  position: absolute;
  right: 0;
  top: 0;

  button {
    background-color: rgba(255, 255, 255, 0.9);

    &:hover {
      background-color: rgba(255, 255, 255, 0.8);
    }
  }
`;
const StyledPhotoWrapper = styled.div`
  background-color: whitesmoke;
  background-image: ${({
  url
}) => `url(${url})`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  flex-shrink: 0;
  height: 175px;
`;
const StyledHeader = styled.div`
  display: flex;
  flex-shrink: 0;
  padding: 16px 16px 8px 16px;

  &.shifted {
    padding: 16px 54px 8px 16px;
  }
`;
const StyledHeaderLogo = styled.div`
  align-self: center;
  background-image: ${({
  url
}) => `url(${url})`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  height: 48px;
  margin-right: 8px;
  width: 48px;
`;
const StyledTitle = styled(_Typography)`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const StyledContent = styled.div`
  flex-shrink: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
`;
const StyledActions = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
  gap: 8px;
  height: 48px;
  padding: 0 16px;
`;
export default /*#__PURE__*/forwardRef(PoiPopup);